import DesignTokens from '@tableau/design-tokens';
import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, TextAreaWidget } from '@tableau/widgets';
import * as React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { characterCounter } from '~src/commonInfra/utils/CharacterCounter';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { applyColor }from '~src/style/ColorService';

import { CloseAndDismiss, ModalInstance, ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { IRequestAccessForContentParams, RequestAccessForContentRequest } from '../serverApi/requests/RequestAccessForContentRequest';
import { sendRequestAccessClickedTelemetry, sendRequestAccessDisplayedTelemetry } from '../telemetry/RequestAccessTelemetry';
import { RequestAccessType, RequestAccessTypeValue } from '../types/RequestAccessType';

export interface RequestAccessContent {
  identifier: string;
  type: RequestAccessType;
  name?: string;
}

interface MessagesDialogExplanation {
  explanation_withName: (a: { ITEM_NAME: string }) => string;
  explanation_noName: () => string;
}

const TypeToMessagesMap = new Map<RequestAccessType, MessagesDialogExplanation>()
  .set(RequestAccessTypeValue.collection, {
    explanation_noName: Messages.requestAccessBodyCollection_noName,
    explanation_withName: Messages.requestAccessBodyCollection_withName,
  })
  .set(RequestAccessTypeValue.datasource, {
    explanation_noName: Messages.requestAccessBodyDatasource_noName,
    explanation_withName: Messages.requestAccessBodyDatasource_withName,
  })
  .set(RequestAccessTypeValue.flow, {
    explanation_noName: Messages.requestAccessBodyFlow_noName,
    explanation_withName: Messages.requestAccessBodyFlow_withName,
  })
  .set(RequestAccessTypeValue.project, {
    explanation_noName: Messages.requestAccessBodyProject_noName,
    explanation_withName: Messages.requestAccessBodyProject_withName,
  })
  .set(RequestAccessTypeValue.view, {
    explanation_noName: Messages.requestAccessBodyView_noName,
    explanation_withName: Messages.requestAccessBodyView_withName,
  })
  .set(RequestAccessTypeValue.virtualConnection, {
    explanation_noName: Messages.requestAccessBodyVirtualConnection_noName,
    explanation_withName: Messages.requestAccessBodyVirtualConnection_withName,
  })
  .set(RequestAccessTypeValue.virtualConnectionTable, {
    explanation_noName: Messages.requestAccessBodyVirtualConnectionTable_noName,
    explanation_withName: Messages.requestAccessBodyVirtualConnectionTable_withName,
  })
  .set(RequestAccessTypeValue.workbook, {
    explanation_noName: Messages.requestAccessBodyWorkbook_noName,
    explanation_withName: Messages.requestAccessBodyWorkbook_withName,
  });


export namespace RequestAccessDialog {
  const DIALOG_WIDTH = 400;
  export const DIALOG_TESTID = 'request-access';
  export const DISMISS_BUTTON_TESTID = DIALOG_TESTID + '-dismiss-button';
  export const REQUEST_ACCESS_BUTTON_TESTID = DIALOG_TESTID + '-request-access-button';
  export const DIALOG_TEXT_AREA_TESTID = DIALOG_TESTID + '-text-area';
  export const DIALOG_PERMISSION_REQUIRED_TEXT_TESTID = DIALOG_TESTID + '-permission-required-text';
  export const DIALOG_EXPLANATORY_TEXT_TESTID = DIALOG_TESTID + '-explanatory-text';
  export const DIALOG_TEXT_AREA_CHARACTER_COUNTER_TESTID = DIALOG_TESTID + '-character-counter';
  export const DIALOG_TEXT_AREA_MAX_LENGTH = 500;

  export const enum DialogResult {
    Dismiss = 'dismiss',
    RequestAccess = 'requestAccess',
  }

  export interface RequestAccessDialogProps {
    content: RequestAccessContent;
    onDismiss: () => void;
    onRequestAccess: (message?: string) => void;
  }

  export interface RequestAccessDialogState {
    message: string;
  }

  export class RequestAccessDialog extends React.PureComponent<RequestAccessDialogProps, RequestAccessDialogState> {
    public state: RequestAccessDialogState = {
      message: '',
    };

    private handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      this.setState({ message: e.target.value, });
    };

    private getExplanatoryText = (): string => {
      const messages = TypeToMessagesMap.get(this.props.content.type);
      return this.props.content.name !== undefined
       ? messages.explanation_withName({ ITEM_NAME: this.props.content.name, })
       : messages.explanation_noName();
    };

    private renderContent = (): JSX.Element => {
      return (
        <div style={{ marginBottom: '20px'}}>
          <div style={{ fontWeight: 'normal', marginBottom: '20px', fontSize: '16px', lineHeight: '18px', color: applyColor(DesignTokens.color.font.default.secondary, '#4F4F4F'), letterSpacing: '0.2px' }}>
              <p data-tb-test-id={DIALOG_PERMISSION_REQUIRED_TEXT_TESTID} style={{marginTop: '0px'}}>{Messages.requestAccessPermissionRequired()}</p>
          </div>
          <div style={{ fontWeight: 'normal', fontSize: '12px', lineHeight: '18px', color: applyColor(DesignTokens.color.font.default.secondary, '#4F4F4F'), letterSpacing: '0.2px' }}>
            <p data-tb-test-id={DIALOG_EXPLANATORY_TEXT_TESTID} style={{marginTop: '0px'}}>{this.getExplanatoryText()}</p>
            <TextAreaWidget
              label={Messages.requestAccessMessageLabel()}
              message={<div data-tb-test-id={DIALOG_TEXT_AREA_CHARACTER_COUNTER_TESTID}>{
                characterCounter({ value: this.state.message, maximum: DIALOG_TEXT_AREA_MAX_LENGTH })
              }</div>}
              onChange={this.handleMessageChange}
              maxLength={DIALOG_TEXT_AREA_MAX_LENGTH}
              rows={3}
              style={{width: '364px'}}
              testId={DIALOG_TEXT_AREA_TESTID}
              value={this.state.message}
            />
          </div>
        </div>
      );
    };

    private requestAccess = (): void => {
      sendRequestAccessClickedTelemetry(this.props.content.type);

      const params: IRequestAccessForContentParams = {
        contentIdentifier: this.props.content.identifier,
        contentType: this.props.content.type,
        requestMessage: this.state.message
      };

      const onSuccess = () => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.requestAccessSubmitted(),
        });
      };

      const onError = () => ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.requestAccessError()
      });

      WebHttpClient.sendRequest(new RequestAccessForContentRequest(params))
      .then(rejectIfResultContainsError)
      .then(onSuccess, onError)
      .finally(this.props.onRequestAccess);
    };

    private renderFooter = (): JSX.Element => {
      return (
        <DialogFooterButtonsWidget
          buttons={[
            {
              buttonType: ButtonType.Outline,
              children: Messages.cancel(),
              handleClick: this.props.onDismiss,
              testId: DISMISS_BUTTON_TESTID,
            }, {
              buttonType: ButtonType.Primary,
              children: Messages.requestAccess(),
              handleClick: this.requestAccess,
              testId: REQUEST_ACCESS_BUTTON_TESTID,
            }
          ]}
        />
      );
    };

    public render(): JSX.Element {
      return (
      <DialogWidget
        dialogWidth={DIALOG_WIDTH}
        isModal={true}
        onCloseRequest={this.props.onDismiss}
        positioningBehavior={DialogPositioningBehavior.Centered}
        showCloseButton={false}
        showFooter={true}
        footerContent={this.renderFooter()}
        handleGlassClick={this.props.onDismiss}
        testId={DIALOG_TESTID}
        title={Messages.requestAccess()}
        type={DialogType.NormalWithoutTitleBar}
      >
        {this.renderContent()}
      </DialogWidget>
      );
    }
  }

export const createRequestAccessDialog = (content: RequestAccessContent, onRequestAccess: (message?: string) => void) =>
  (closeAndDismiss: CloseAndDismiss<undefined>): React.ReactElement => {
    const {close, dismiss} = closeAndDismiss;
    return <RequestAccessDialog
      content={content}
      onDismiss={close}
      onRequestAccess={onRequestAccess}
    />;
  };

  export function open(content: RequestAccessContent) {
    let modalInstance: ModalInstance<DialogResult>;
    sendRequestAccessDisplayedTelemetry(content.type);

    const onDismiss = (): void => {
      modalInstance && modalInstance.close(DialogResult.Dismiss);
    };

    const onRequestAccess = () => {
      modalInstance?.close(DialogResult.RequestAccess);
    };

    modalInstance = ReactModalService.openCustom(createRequestAccessDialog(content, onRequestAccess));
    return modalInstance.result;
  }
}
